/*eslint max-lines: ["error", 700]*/
import {Component, OnInit} from '@angular/core';
import {ConfigService as AnalyticsConfigService} from '@wdpr/angular-analytics';
import {AnalyticsHandler} from '../../shared/libraries/analytics-handler';
import {ApiService} from '../../shared/services/api.service';
import {ConfigService} from '../../shared/services/config.service';
import {TranslatePipe} from '../../shared/pipes/translate.pipe';
import {DateHandler} from '../../shared/libraries/date-handler';
import {Constants} from '../../shared/constants';
import {Utils} from '../../shared/libraries/utils';
import values from 'lodash-es/values';
import includes from 'lodash-es/includes';
import { SegmentHandler } from 'src/app/shared/libraries/segment-handler';

@Component({
    selector: 'app-availability-calendar',
    templateUrl: './availability-calendar.component.html',
    styleUrls: ['./availability-calendar.component.scss'],
    providers: [TranslatePipe]
})
export class AvailabilityCalendarComponent implements OnInit {
    /**
     * Available dates
     * @var {Array<any>}
     */
    public availableDates: Array<any> = [];

    /**
     * Availability types (full, partial, none)
     * @var {object}
     */
    public availabilityTypes: object = {};

    /**
     * Current date range for API calls
     * @var {any}
     */
    public currentDateRange: any = {};

    /**
     * Selected date
     * @var {any}
     */
    public selectedDate: any = {};

    /**
     * Selected availability segment
     * @var {string}
     */
    public selectedSegment = '';

    /**
     * Available reservations title
     * @var {string}
     */
    public reservationsTitle = '';

    /**
     * Available Calendar legend title - only screen reader
     * @var {string}
     */
    public legendTitle = '';

    /**
     * Reservations footer disclaimer
     * @var {string}
     */
    public reservationsDisclaimer = '';

    /**
     * List of all parks
     * @var {Array<any>}
     */
    public parksList: Array<any> = [];

    /**
     * Calendar end date
     * @var {string}
     */
    public calendarEndDate: string;

    /**
     * Property to show or hide the Availability Information Component
     * @var {boolean}
     */
     public showAIComponent = false;

    /**
     * Application configuration
     * @var {any}
     */
    private _config: any = {};

    /**
     * Current date
     * @var {string}
     */
    private _today: string;

    /**
     * Availability restrictions (max date the calendar will show)
     * @var {any}
     */
    private _availabilityRestrictions: any = {};

    /**
     * Debounce timeout for calendar requests
     * @var {any}
     */
    private _calendarDebounceTimeout: any;

    /**
     * Flag to check if page load analytics event was fired
     * @var {boolean}
     */
    private _pageLoadAnalyticsTriggered = false;

    /**
     * Error message
     * @var {string}
     */
    public errorMessage: string = null;

    /**
     * Default date selected
     * @var {string}
     */
    public defaultDate: string;

    /**
     * Flag to check if the default selected date was used or not
     * @var {boolean}
     */
    private _defaultSelectedDateUsed = false;

    /**
     * Flag to check if the current date was used to get the avail dates
     * @var {boolean}
     */
    private _defaultCurrentDateRangeUsed = false;

    /**
     * @desc  sets date type map
     */
    public dateTypeMap: any = {};

    /**
     * @desc  Flag to indicate if calendar should show exactly the number of months
     * specified in the monthsToDisplay attribute
     * since for Avail calendar monthsToDisplay is 1.. it will subtract 1 month for current month
     * meaning next month is disabled
     */
    public displayStrictNumMonths = false;

    /**
     * @desc Selected dates for com-calendar
     */
    public selectedDates: Array<String>;

    /**
     * @desc keeps last selectedDate to be shown in calendar
     */
    public lastSelectedDates = [];

    /**
     * @desc show/hide avl calendar component
     */
    public hideAvailabilityCalendar = false;

    /**
     * Class constructor
     *
     * @param _analyticsHandler
     * @param _apiService
     * @param _configService
     * @param _translatePipe
     * @param _dateHandler
     * @param _analyticsConfig
     */
    constructor(
        private _analyticsHandler: AnalyticsHandler,
        private _apiService: ApiService,
        private _configService: ConfigService,
        private _translatePipe: TranslatePipe,
        private _dateHandler: DateHandler,
        private _analyticsConfig: AnalyticsConfigService,
        private _segmentHandler: SegmentHandler
    ) {
        this._config = this._configService.getConfig();
    }

    /**
     * On init method
     */
    ngOnInit() {
        this._availabilityRestrictions = this._config.availabilityConfiguration;


        this.reservationsTitle = 'reservations-available.text';
        this.reservationsDisclaimer = 'footer.disclaimer';
        this.legendTitle = 'availability.legend.text';

        this.parksList = this._config.parks;
        this._calendarDebounceTimeout = null;

        this._initializeDates();
        // set default date only if is valid
        this._setDefaultDate();

        const availabilityTypes = this._config.availabilityDateTypeMap;
        
        for (const index in availabilityTypes) {
            this._getAvailabilityTypes(index, availabilityTypes[index]);
        }

        this._apiService.selectedSegment.subscribe(
            (segment) => {
                if (!!segment) {
                    this.selectedSegment = segment;
                    this.selectedDate = {};
                    this.getAvailabilityForSelectedSegment();
                }
            }
        );

        this._analyticsConfig.autoPageViewTrackingTiming = Constants.ANALYTICS_PAGE_VIEW_TRACKING_TIME;
        
        this.hideAvailabilityCalendar = this._segmentHandler.getBlacklistedSegments().includes(this.selectedSegment) || this._segmentHandler.getSegments().length === 0;
    }

    /**
     * Get availability data for the selected segment
     *
     * @returns {void}
     */
    public getAvailabilityForSelectedSegment(event?: any): void {
        if (!event) {
            this.availableDates = [];
            this._setDateRange();

            // clear error message prior to make another call.
            this.errorMessage = null;

            this._apiService
                .setRequestHeaders()
                .setRequestParams({
                    ...{'segment': this.selectedSegment},
                    ...this.currentDateRange
                })
                .getAvailability().subscribe(
                    (response: any) => {
                        this.availableDates = response;

                        if (!this._pageLoadAnalyticsTriggered) {
                            setTimeout(() => {
                                this._trackPageViewEvent(this.availableDates);
                            });
                        }
                        this._pageLoadAnalyticsTriggered = true;
                    },
                    (error: Error) => {
                        this.errorMessage = 'error.generic';
                    }
                );
        } else {
            // If this was triggered because month changed, send data to analytics framework
            this._sendDataToAnalytics(
                Constants[`ANALYTICS_LINK_ID_MONTH_${event.detail.direction.toUpperCase()}`],
                Constants[`ANALYTICS_EVENT_TYPE_MONTH_${event.detail.direction.toUpperCase()}`],
                this.availableDates
            );
        }
    }

    /**
     * Generate the availability types to show below the calendar
     *
     * @private
     * @returns {void}
     */
    private _getAvailabilityTypes(index, typeKey) {

        if (index === 'none') {
            this.dateTypeMap.none = {
                class: this._translatePipe.transform(`${typeKey}.class`),
                content: this._translatePipe.transform(`${typeKey}.content`)
            };
        } else {
            this.dateTypeMap[index] = {
                a11yContent: this._translatePipe.transform(`${typeKey}.a11yContent`),
                class: this._translatePipe.transform(`${typeKey}.class`),
                content: this._translatePipe.transform(`${typeKey}.content`),
                displayName: this._translatePipe.transform(`${typeKey}.displayName`),
                displayOrder: this._translatePipe.transform(`${typeKey}.displayOrder`),
                visibility: this._translatePipe.transform(`${typeKey}.visibility`),
            };

            if (index !== 'unavailable') {
                this.dateTypeMap[index].isIcon = this._translatePipe.transform(`${typeKey}.isIcon`);
            }
        }

        if (index === 'all') {
            this.dateTypeMap.all.firstClass = this._translatePipe.transform(`${typeKey}.firstClass`);
            this.dateTypeMap.all.firstIcon = this._translatePipe.transform(`${typeKey}.firstIcon`);
            this.dateTypeMap.all.isOverlappingIcon = this._translatePipe.transform(`${typeKey}.isOverlappingIcon`);
            this.dateTypeMap.all.secondClass = this._translatePipe.transform(`${typeKey}.secondClass`);
            this.dateTypeMap.all.secondIcon = this._translatePipe.transform(`${typeKey}.secondIcon`);
        }
    }

    /**
     * Searchs the default date on the available dates
     *
     * @param availableDates
     * @param defaultDate
     * @private
     * @returns {boolean}
     */
    private _searchDefaultDate(availableDates, defaultDate) {
        for (let i = 0; i < availableDates.length; i++) {
            if (availableDates[i].date === defaultDate) {
                return true;
            }
        }

        return false;
    }

    /**
     * Sets the default date from query param
     *
     * @private
     * @returns {void}
     */
    private _setDefaultDate(): void {
        const defaultDate = Utils.getValueFromQueryParam(Constants.PARAM_DEFAULT_DATE);
        if (this._validateDefaultDate(defaultDate)) {
            // remove any date if stored.
            sessionStorage.removeItem('selectedDate');
            this.defaultDate = defaultDate;
        }
    }

    /**
     * Sets the selected date information
     *
     * @param date
     * @private
     * @returns {void}
     */
    private _setSelectedDate(date: any): void {
        if (
            !this._defaultSelectedDateUsed &&
            this._validateDefaultDate(this.defaultDate) &&
            this.defaultDate >= date &&
            this._searchDefaultDate(this.availableDates, this.defaultDate)
        ) {
            date = this.defaultDate;
        } else {
            sessionStorage.removeItem('selectedDate');
        }

        this._defaultSelectedDateUsed = true;

        // save the guest selected date
        sessionStorage.setItem('selectedDate', date);
        date = this._dateHandler.convertDateToMomentObject(date);

        this.selectedDate = {
            raw: this._dateHandler.formatDate(Constants.DATE_RAW, date),
            formatted: this._dateHandler.formatDate(Constants.DATE_PRETTY, date),
            availability: []
        };

        this.selectedDate.availability = this.availableDates.filter((availableDate: any) => {
            return availableDate.date === this.selectedDate.raw;
        })[0];
    }

    /**
     * Get detailed park availability for the selected date
     *
     * @param event Polymer event
     * @returns {void}
     */
    public getParksAvailability(event): void {
        // Shouldn't try to get availability if we don't have a date selected
        if (event.detail && !this._hasSelectedDates(event.detail)) {
            return;
        }

        this.selectedDates = event.detail.selectedDates;
        this.lastSelectedDates = this.selectedDates;

        let isAvailable = false;
        let availableParks;
        const analyticsLinkId = (!this._pageLoadAnalyticsTriggered)
            ? Constants.ANALYTICS_LINK_ID_INITIAL_AVAIL
            : Constants.ANALYTICS_LINK_ID_CHECK_AVAIL;

        this._setSelectedDate(event.detail.selectedDates[0]);
        availableParks = this.selectedDate.availability.facilities.filter((facility) => {
            return facility.available === true;
        });

        this.selectedDate.availability = [];

        for (const park of this.parksList) {

            availableParks.forEach(availablePark => {
                if (park.parkId === availablePark.facilityName) {
                    isAvailable = availablePark.available;
                }
            });

            this.selectedDate.availability.push({
                parkId: park.parkId,
                parkName: this._translatePipe.transform(`park.${park.parkId}`),
                parkAnalyticsName: park.parkAnalyticsName,
                available: isAvailable,
                class: isAvailable ? Constants.PARK_AVAILABLE : Constants.PARK_UNAVAILABLE,
                availLabel: this._translatePipe.transform('park-available'),
                unavailLabel: this._translatePipe.transform('park-not-available')
            });
        }

        if (!this._pageLoadAnalyticsTriggered) {
            this._pageLoadAnalyticsTriggered = true;
        }

        this._sendDataToAnalytics(
            analyticsLinkId,
            Constants.ANALYTICS_EVENT_TYPE_DATE_CHANGED,
            this.selectedDate.availability
        );
    }

    /**
     * Initializes date strings
     *
     * @private
     * @returns {void}
     */
    private _initializeDates(): void {
        this._today = this._dateHandler.formatDate(Constants.DATE_RAW);
        this.calendarEndDate = this._getAvailabilityRestriction();
    }

    /**
     * @desc Validate selectedDates exists and is not empty in value
     * 
     * @param value
     * @private
     * 
     * @return {boolean}
     */
    private _hasSelectedDates(value) {
        return value
        && value.selectedDates
        && Array.isArray(value.selectedDates)
        && value.selectedDates.length > 0;
    }

    /**
     * Gets the availability restriction value
     *
     * @private
     * @returns {string}
     */
    private _getAvailabilityRestriction(): string {
        let availabilityRestrictionType = this._availabilityRestrictions.type,
            availabilityRestrictionValue = this._availabilityRestrictions.value,
            maxDate;

        if (!includes(values(Constants.AVAILABILITY_RESTRICTION_TYPES), availabilityRestrictionType)) {
            availabilityRestrictionType = Constants.AVAILABILITY_RESTRICTION_TYPES.range;
            availabilityRestrictionValue = Constants.AVAILABILITY_RESTRICTION_DEFAULT_RANGE;
        }

        switch (availabilityRestrictionType) {
            case Constants.AVAILABILITY_RESTRICTION_TYPES.specific:
                maxDate = this._dateHandler.convertDateToMomentObject(availabilityRestrictionValue || this._today);
                break;
            case Constants.AVAILABILITY_RESTRICTION_TYPES.range:
            default:
                maxDate = this._dateHandler.convertDateToMomentObject(this._today)
                    .add(availabilityRestrictionValue, 'days');
                break;
        }

        return this._dateHandler.formatDate(Constants.DATE_RAW, maxDate);
    }

    /**
     * Sets the date range to invoke the availability API
     *
     * @private
     *
     * @returns {void}
     */
    private _setDateRange(): void {
        this.currentDateRange = {
            'startDate': this._dateHandler.formatDate(
                Constants.DATE_RAW, this._dateHandler.convertDateToMomentObject(this._today)
            ),
            'endDate': this._dateHandler.formatDate(
                Constants.DATE_RAW, this.calendarEndDate
            )
        };
    }

    /**
     * Sends data to analytics framework
     *
     * @param linkId
     * @param eventType
     * @param parksAvailability
     * @private
     * @returns {void}
     */
    private _sendDataToAnalytics(linkId: string, eventType: string, parksAvailability: any): void {
        this._analyticsHandler
            .setLinkId(linkId)
            .setSelectedDate(this.selectedDate.raw)
            .setCurrentDate(this.currentDateRange.startDate)
            .setSelectedSegment(this.selectedSegment)
            .setParksAvailability(parksAvailability)
            .trackAnalytics(eventType);
    }

    /**
     * Tracks the page view event
     *
     * @param parksAvailability
     * @private
     * @returns {void}
     */
    private _trackPageViewEvent(parksAvailability: any): void {
        this._analyticsHandler
            .setSelectedDate(this.selectedDate.raw)
            .setCurrentDate(this.currentDateRange.startDate)
            .setSelectedSegment(this.selectedSegment)
            .setParksAvailability(parksAvailability)
            .trackPageViewEvent();
    }

    /**
     * Validates the default date
     *
     * @param date
     * @private
     * @returns {boolean}
     */
    private _validateDefaultDate(defaultDate: string): boolean {

    const momentDefaultDate = this._dateHandler.convertDateToMomentObject(defaultDate);
    const momentTodayDate = this._dateHandler.convertDateToMomentObject(this._today);
    const diff = momentDefaultDate.diff(momentTodayDate, 'days');

    if (momentDefaultDate.isValid() &&
        defaultDate >= this._today &&
        diff <= Constants.AVAILABILITY_RESTRICTION_DEFAULT_RANGE &&
        (momentDefaultDate.isBefore(this.calendarEndDate) || momentDefaultDate.isSame(this.calendarEndDate))) {
            return true;
        }

        return false;
    }
}
